/**
 * Utility functions for downloading files from various sources
 */

interface DownloadOptions {
  fileName: string;
  filePath: string;
  fallbackToNewTab?: boolean;
}

/**
 * Downloads a file from the given URL
 * Handles Azure blob storage URLs with SAS tokens
 * Falls back to opening in new tab if download fails
 */
export const downloadFile = async ({
  fileName,
  filePath,
  fallbackToNewTab = true,
}: DownloadOptions): Promise<void> => {
  if (!filePath) {
    throw new Error('No file path provided');
  }

  try {
    // Method 1: Direct download link
    // This works for most cases including Azure blob storage with proper headers
    const link = window.document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    // Add to DOM, click, and remove
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);

    return;
  } catch (error) {
    console.warn('Direct download failed, trying fetch method:', error);
  }

  try {
    // Method 2: Fetch and create blob
    // This bypasses some CORS issues but requires the server to allow it
    const response = await fetch(filePath, {
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Accept: '*/*',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Download failed: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);

    // Clean up the blob URL
    window.URL.revokeObjectURL(url);

    return;
  } catch (error) {
    console.warn('Fetch download failed:', error);

    if (fallbackToNewTab) {
      // Last resort: open in new tab
      console.info('Falling back to opening file in new tab');
      window.open(filePath, '_blank', 'noopener,noreferrer');
    } else {
      throw error;
    }
  }
};

/**
 * Downloads a file with additional user feedback
 */
export const downloadFileWithFeedback = async (
  options: DownloadOptions,
  onStart?: () => void,
  onSuccess?: () => void,
  onError?: (error: Error) => void
): Promise<void> => {
  try {
    onStart?.();
    await downloadFile(options);
    onSuccess?.();
  } catch (error) {
    const err = error instanceof Error ? error : new Error('Download failed');
    onError?.(err);
    throw err;
  }
};
